import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { memo, useMemo, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { PORTAL_PATH } from '../../../../../../../routes/paths';
import { getAvailableSubpages } from '../../../../../subpages';
import { dictionary as subpagesDictionary } from '../../../../../subpages/dictionary';
import { ClientStatusContext } from '../../../../../../../common/client-status/clientStatusContext';

import { HEIGHT_OF_MOBILE_SUBPAGES } from '../../../../consts';
import { detectIsLabelSelected } from './utils';

const useStyles = makeStyles((theme: Theme) => ({
  subpage: {
    cursor: 'pointer',
    display: 'flex',
    background: theme.palette.primary.dark,
    borderBottom: '2px solid transparent',
    color: theme.palette.common.white,
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    flex: 1
  },
  selectedSubpage: {
    cursor: 'auto',
    color: theme.palette.primary.light,
    justifyContent: 'center',
    borderBottom: '2px solid #18C67F'
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    background: theme.palette.primary.dark,
    height: HEIGHT_OF_MOBILE_SUBPAGES,
    justifyContent: 'center',
    flexDirection: 'row',
    width: '100%'
  }
}));

const pathSplitter = (path: string): string[] => path.split('/').filter(pathItem => pathItem !== '');

const Subpages = () => {
  const history = useHistory();
  const classes = useStyles();
  const { pathname } = useLocation();
  const { checkIfUserHasPortalPrivilege, checkIfUserHasApiPrivilege } = useContext(ClientStatusContext);
  const [selectedUnformattedPage, selectedUnformattedSubpage] = pathSplitter(pathname) as [string, string | undefined];
  const availableSubpagesConfig = useMemo(() => subpagesDictionary[`/${selectedUnformattedPage}`], [
    selectedUnformattedPage
  ]);

  const currentMainRoute = `/${selectedUnformattedPage}` as PORTAL_PATH;

  const availableSubpages = useMemo(
    () =>
      availableSubpagesConfig
        ? getAvailableSubpages(checkIfUserHasPortalPrivilege, checkIfUserHasApiPrivilege, currentMainRoute).map(
            ({ title, path }) => ({
              label: title,
              onClick: () => {
                history.push(path);
              }
            })
          )
        : null,
    [availableSubpagesConfig, checkIfUserHasApiPrivilege, checkIfUserHasPortalPrivilege, currentMainRoute, history]
  );

  const subpageTabs = () => {
    if (availableSubpages) {
      return availableSubpages.map(currentSubpage => (
        <Box
          key={currentSubpage.label}
          className={
            selectedUnformattedSubpage && detectIsLabelSelected(currentSubpage.label, selectedUnformattedSubpage)
              ? clsx(classes.subpage, classes.selectedSubpage)
              : classes.subpage
          }
          onClick={currentSubpage.onClick}
        >
          <Typography fontSize="16px" color="inherit">
            {currentSubpage.label}
          </Typography>
        </Box>
      ));
    }
    return null;
  };

  return <Box className={classes.container}>{subpageTabs()}</Box>;
};

export default memo(Subpages);
